import { Route, Routes, useLocation } from "react-router-dom";
import { ThemeProvider } from "./Components/ThemProvider";
import Header from "./Components/Header";
import HeroSection from "./Components/HeroSection";
import EducationExperience from "./Components/AboutSection";
import SkillsSection from "./Components/SkillsSection";
import ServicesSection from "./Components/ServicesSection";
import ProjectsSection from "./Components/ProjectsSection";
import CertificationsSection from "./Components/Cretificates";
import ProjectIdeaSection from "./Components/ProjectIdeaSection";
import ContactForm from "./Components/ContactForm";
import Login from "./Components/Login";
import Footer from "./Components/Footer";
import { ScrollProgress } from "./Components/ScrollProgress";
import { BackToTop } from "./Components/BackToTop";
import { SectionDivider } from "./Components/SectionDivider";
import { NeuralNetwork } from "./Components/NeuralNetwork";
import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import RifathLogo from "./Components/RifathLogo";
import { useIsMobile } from "./hooks/useIsMobile";

const useIntersectionObserver = () => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.05 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return { ref, isVisible };
};

const Section = ({
  id,
  children,
}: {
  id: string;
  children: React.ReactNode;
}) => {
  const { ref, isVisible } = useIntersectionObserver();

  return (
    <div
      id={id}
      ref={ref}
      className={`transition-all ease-out ${
        isVisible
          ? "opacity-100 translate-y-0 duration-500"
          : "opacity-0 translate-y-4 sm:translate-y-8 duration-700"
      }`}
    >
      {children}
    </div>
  );
};

const LoadingScreen = () => {
  return (
    <motion.div
      className="fixed inset-0 flex flex-col items-center justify-center bg-white dark:bg-surface-950 z-50"
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Animated background blobs */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute -top-40 -right-40 w-80 h-80 bg-primary-500/10 rounded-full blur-3xl"
          animate={{ scale: [1, 1.3, 1] }}
          transition={{ duration: 3, repeat: Infinity }}
        />
        <motion.div
          className="absolute -bottom-40 -left-40 w-96 h-96 bg-accent-500/10 rounded-full blur-3xl"
          animate={{ scale: [1.3, 1, 1.3] }}
          transition={{ duration: 3, repeat: Infinity }}
        />
      </div>

      <div className="relative z-10 flex flex-col items-center">
        {/* Logo */}
        <motion.div
          className="mb-8"
          initial={{ scale: 0.5, opacity: 0, rotate: -10 }}
          animate={{ scale: 1, opacity: 1, rotate: 0 }}
          transition={{ duration: 0.5, type: "spring" }}
        >
          <RifathLogo size={64} animated={false} />
        </motion.div>

        {/* Progress bar */}
        <div className="w-48 h-1 bg-gray-100 dark:bg-surface-800 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-primary-500 to-accent-500 rounded-full"
            initial={{ width: "0%" }}
            animate={{ width: "100%" }}
            transition={{ duration: 1.2, ease: "easeInOut" }}
          />
        </div>

        <motion.p
          className="mt-4 text-sm text-gray-400 dark:text-gray-500 font-medium"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          Loading...
        </motion.p>
      </div>
    </motion.div>
  );
};

const App: React.FC = () => {
  const location = useLocation();
  const isLoginRoute = location.pathname === "/login";
  const [isLoading, setIsLoading] = useState(true);
  const isMobile = useIsMobile();

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 1500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <ThemeProvider>
      <div className="App min-h-screen bg-white dark:bg-surface-950 text-gray-900 dark:text-gray-100 transition-colors duration-300 global-bg">
        {/* Animated background layers — hidden on mobile for performance */}
        {!isMobile && (
          <>
            <div className="bg-orb bg-orb-1" />
            <div className="bg-orb bg-orb-2" />
            <div className="bg-orb bg-orb-3" />
            <div className="bg-orb bg-orb-4" />
            <div className="bg-orb bg-orb-5" />
            <div className="grid-pattern" />
          </>
        )}

        {/* Neural network background — desktop only */}
        {!isLoginRoute && !isMobile && <NeuralNetwork />}

        <AnimatePresence>{isLoading && <LoadingScreen />}</AnimatePresence>

        {/* Skip to main content link */}
        {!isLoginRoute && (
          <a href="#main-content" className="skip-to-main">
            Skip to main content
          </a>
        )}

        {/* Scroll progress indicator */}
        {!isLoginRoute && <ScrollProgress />}

        {!isLoginRoute && <Header />}

        <main id="main-content" className="relative z-10">
          <Routes>
            <Route
              path="/"
              element={
                <>
                  {/* PHASE 1: INTRODUCTION */}
                  <Section id="hero">
                    <HeroSection />
                  </Section>

                  <SectionDivider variant="gradient" />

                  <Section id="about">
                    <EducationExperience />
                  </Section>

                  {/* PHASE 2: CAPABILITIES */}
                  <SectionDivider variant="dots" />

                  <Section id="skills">
                    <SkillsSection />
                  </Section>

                  <SectionDivider variant="gradient" />

                  <Section id="services">
                    <ServicesSection />
                  </Section>

                  {/* PHASE 3: PROOF */}
                  <SectionDivider variant="dots" />

                  <Section id="projects">
                    <ProjectsSection />
                  </Section>

                  <SectionDivider variant="gradient" />

                  <Section id="certification">
                    <CertificationsSection />
                  </Section>

                  {/* PHASE 4: ENGAGEMENT */}
                  <SectionDivider variant="dots" />

                  <Section id="cta">
                    <ProjectIdeaSection />
                  </Section>

                  <SectionDivider variant="gradient" />

                  <Section id="contact">
                    <ContactForm />
                  </Section>
                </>
              }
            />
            <Route path="/login" element={<Login />} />
          </Routes>
        </main>

        {/* Back to top button */}
        {!isLoginRoute && <BackToTop />}

        {!isLoginRoute && <Footer />}
      </div>
    </ThemeProvider>
  );
};

export default App;
